import React, { useEffect, useMemo, useState } from 'react';

import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { gridFilterModelSelector, gridSortModelSelector } from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';
import {
  Bookmark,
  Circle,
  CircleCheck,
  LayoutList,
  Plus,
  Save,
  UserRound,
  UserRoundCheck,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { DropdownSelectButton, NameDialog, NameDialogVariant } from 'src/components';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  applyChartsIntegrationToGrid,
  getAppliedWorkflowListStateFromWorkflowView,
  getWorkflowViewStateFromWorkflowList,
} from './workflow-list-charts';
import type { WorkflowListChartsIntegrationSlice } from './workflow-list-charts';
import type { WorkflowListPresetId } from './workflow-views';
import {
  getIsGridFilterModel,
  getIsGridSortModel,
  getSavedWorkflowViewsForPicker,
  getWorkflowViewsTriggerLabel,
  WORKFLOW_LIST_PRESET_DEFINITIONS,
  WORKFLOW_LIST_PRESET_IDS,
} from './workflow-views';

const useStyles = makeStyles()(theme => ({
  customHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    justifyContent: 'space-between',
    lineHeight: '32px',
    width: '100%',
  },
  emptyWorkflowViews: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5, 2, 1.5),
  },
  menu: {
    minWidth: 280,
  },
  menuItemIcon: {
    '&.MuiListItemIcon-root': {
      minWidth: 32,
    },
  },
  sectionHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '32px',
    width: '100%',
  },
  viewName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const WORKFLOW_LIST_PRESET_ICON = {
  all: LayoutList,
  'assigned-to-me': UserRoundCheck,
  completed: CircleCheck,
  open: Circle,
  'requested-by-me': UserRound,
} as const satisfies Record<WorkflowListPresetId, typeof Circle>;

interface WorkflowViewsMenuProps {
  chartsIntegrationSlice: WorkflowListChartsIntegrationSlice;
  listApiRef: React.RefObject<GridApiPremium | null> | null;
  onActiveWorkflowViewIdChange: (viewId: string | null) => void;
  onChartsIntegrationSliceChange: (slice: WorkflowListChartsIntegrationSlice) => void;
  onWorkflowListPresetChange: (presetId: WorkflowListPresetId) => void;
  workflowListPreset: WorkflowListPresetId;
  activeWorkflowViewId?: string | null;
}

export function WorkflowViewsMenu({
  activeWorkflowViewId = null,
  chartsIntegrationSlice,
  listApiRef,
  onActiveWorkflowViewIdChange,
  onChartsIntegrationSliceChange,
  onWorkflowListPresetChange,
  workflowListPreset,
}: WorkflowViewsMenuProps): JSX.Element | null {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [, setGridStateRevision] = useState(0);

  const { data: workflowViewsData, refetch: refetchWorkflowViews } =
    Apollo.useGetWorkflowViewsQuery({
      skip: !listApiRef,
    });
  const [createWorkflowView] = Apollo.useCreateWorkflowViewMutation();
  const [updateWorkflowView, { loading: updatingView }] = Apollo.useUpdateWorkflowViewMutation();

  const workflowViews = useMemo(
    () => getSavedWorkflowViewsForPicker(workflowViewsData?.workflowViews ?? []),
    [workflowViewsData?.workflowViews]
  );
  const activeWorkflowView = workflowViews.find(view => view.id === activeWorkflowViewId) ?? null;
  const activeWorkflowViewAppliedState = useMemo(
    () =>
      activeWorkflowView
        ? getAppliedWorkflowListStateFromWorkflowView(activeWorkflowView.state)
        : null,
    [activeWorkflowView]
  );
  const triggerLabel = getWorkflowViewsTriggerLabel({
    activeWorkflowViewName: activeWorkflowView?.name ?? null,
    workflowListPreset,
  });
  const canUpdateActiveView = Boolean(activeWorkflowView);

  const closeMenu = () => setMenuOpen(false);

  const readListState = (
    chartsIntegration: WorkflowListChartsIntegrationSlice = chartsIntegrationSlice
  ) => {
    if (!listApiRef) return null;
    const gridApi = listApiRef.current;
    if (!gridApi) return null;
    const filterModel = gridFilterModelSelector(listApiRef);
    const sortModel = gridSortModelSelector(listApiRef);
    const rowGroupingModel = gridApi.state.rowGrouping?.model ?? [];
    const aggregationModel = gridApi.state.aggregation?.model ?? undefined;
    return getWorkflowViewStateFromWorkflowList({
      aggregationModel,
      chartsIntegration,
      filterModel,
      rowGroupingModel,
      sortModel,
    });
  };

  useEffect(() => {
    const gridApi = listApiRef?.current;
    if (!gridApi) return;
    const markStateChanged = () => {
      setGridStateRevision(revision => revision + 1);
    };
    const unsubscribes = [
      gridApi.subscribeEvent('aggregationModelChange', markStateChanged),
      gridApi.subscribeEvent('filterModelChange', markStateChanged),
      gridApi.subscribeEvent('rowGroupingModelChange', markStateChanged),
      gridApi.subscribeEvent('sortModelChange', markStateChanged),
    ];
    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [listApiRef]);

  const activeViewHasChanges = Boolean(
    canUpdateActiveView &&
    activeWorkflowView &&
    activeWorkflowViewAppliedState &&
    !isEqual(
      readListState(activeWorkflowViewAppliedState.chartsIntegration),
      getNormalizedWorkflowViewState(activeWorkflowView.state)
    )
  );

  if (!listApiRef) return null;

  const applyListState = (state: unknown) => {
    if (!listApiRef) return;
    const gridApi = listApiRef.current;
    if (!gridApi) return;
    const applied = getAppliedWorkflowListStateFromWorkflowView(state);
    if (getIsGridFilterModel(applied.filterModel)) {
      gridApi.setFilterModel(applied.filterModel);
    }
    if (getIsGridSortModel(applied.sortModel)) {
      gridApi.setSortModel(applied.sortModel);
    }
    gridApi.setRowGroupingModel(applied.rowGroupingModel);
    gridApi.setAggregationModel(applied.aggregationModel);
    applyChartsIntegrationToGrid(gridApi, applied.chartsIntegration);
    onChartsIntegrationSliceChange(applied.chartsIntegration);
  };

  const selectSystemView = (viewId: WorkflowListPresetId) => {
    onActiveWorkflowViewIdChange(null);
    onWorkflowListPresetChange(viewId);
    closeMenu();
  };

  const selectWorkflowView = (view: (typeof workflowViews)[number]) => {
    applyListState(view.state);
    onActiveWorkflowViewIdChange(view.id);
    closeMenu();
  };

  const openSaveDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    closeMenu();
    setSaveDialogOpen(true);
  };

  const handleSaveView = async (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const state = readListState();
    if (!state) {
      enqueueSnackbar('Workflows list is not ready.', { variant: 'error' });
      return;
    }

    try {
      const result = await createWorkflowView({
        variables: {
          input: {
            name: trimmedName,
            showInSavedViews: true,
            state,
          },
        },
      });
      const savedView = result.data?.createWorkflowView.workflowView;
      await refetchWorkflowViews();
      if (savedView) onActiveWorkflowViewIdChange(savedView.id);
      enqueueSnackbar('View saved.', { variant: 'success' });
      setSaveDialogOpen(false);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save view.', {
        variant: 'error',
      });
    }
  };

  const handleUpdateView = async () => {
    if (!activeWorkflowView || !activeWorkflowViewAppliedState) return;
    // The list editor changes the cohort, not the View's Charts. MUI keeps prior
    // registered Charts around after switching Views, so preserve the selected
    // View's saved Chart slice rather than persisting those stale registrations.
    const state = readListState(activeWorkflowViewAppliedState.chartsIntegration);
    if (!state) {
      enqueueSnackbar('Workflows list is not ready.', { variant: 'error' });
      return;
    }

    try {
      await updateWorkflowView({
        variables: {
          input: {
            id: activeWorkflowView.id,
            props: { state },
          },
        },
      });
      await refetchWorkflowViews();
      enqueueSnackbar('View updated.', { variant: 'success' });
      closeMenu();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update view.', {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <DropdownSelectButton
        ref={setAnchorEl}
        icon={<Bookmark size={16} />}
        label={triggerLabel}
        selected={menuOpen}
        variant="outlined"
        onClick={() => setMenuOpen(!menuOpen)}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        slotProps={{ paper: { className: classes.menu } }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={closeMenu}
      >
        <ListSubheader disableSticky className={classes.sectionHeader}>
          System views
        </ListSubheader>
        {WORKFLOW_LIST_PRESET_IDS.map(viewId => {
          const Icon = WORKFLOW_LIST_PRESET_ICON[viewId];
          return (
            <MenuItem
              key={viewId}
              selected={!activeWorkflowView && workflowListPreset === viewId}
              onClick={() => selectSystemView(viewId)}
            >
              <ListItemIcon className={classes.menuItemIcon}>
                <Icon size={18} />
              </ListItemIcon>
              <ListItemText>{WORKFLOW_LIST_PRESET_DEFINITIONS[viewId].label}</ListItemText>
            </MenuItem>
          );
        })}
        <ListSubheader disableSticky className={classes.customHeader}>
          Saved views
          <AlineTooltip arrow placement="top" title="Save as new view">
            <IconButton aria-label="Save as new view" size="small" onClick={openSaveDialog}>
              <Plus size={16} />
            </IconButton>
          </AlineTooltip>
        </ListSubheader>
        {renderWorkflowViewsMenuItems({
          activeWorkflowViewId: activeWorkflowView?.id,
          classes,
          onSelect: selectWorkflowView,
          workflowViews,
        })}
        {activeViewHasChanges && (
          <>
            <Divider />
            <MenuItem disabled={updatingView} onClick={() => void handleUpdateView()}>
              <ListItemIcon className={classes.menuItemIcon}>
                <Save size={16} />
              </ListItemIcon>
              <ListItemText>Save changes to view</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      <NameDialog
        handleClose={() => setSaveDialogOpen(false)}
        open={saveDialogOpen}
        variant={NameDialogVariant.CreateWorkflowView}
        onFormikSubmit={name => handleSaveView(name)}
      />
    </>
  );
}

function renderWorkflowViewsMenuItems<TView extends { id: string; name: string }>(options: {
  activeWorkflowViewId: string | undefined;
  classes: { emptyWorkflowViews: string; viewName: string };
  onSelect: (view: TView) => void;
  workflowViews: TView[];
}) {
  const { activeWorkflowViewId, classes, onSelect, workflowViews } = options;
  if (!workflowViews.length) {
    return (
      <Typography className={classes.emptyWorkflowViews} variant="body2">
        None yet — use + to save the current filters.
      </Typography>
    );
  }

  return workflowViews.map(view => (
    <MenuItem
      key={view.id}
      selected={activeWorkflowViewId === view.id}
      onClick={() => onSelect(view)}
    >
      <ListItemText className={classes.viewName} primary={view.name} />
    </MenuItem>
  ));
}

function getNormalizedWorkflowViewState(state: unknown) {
  const applied = getAppliedWorkflowListStateFromWorkflowView(state);
  return getWorkflowViewStateFromWorkflowList({
    aggregationModel: applied.aggregationModel,
    chartsIntegration: applied.chartsIntegration,
    filterModel: applied.filterModel,
    rowGroupingModel: applied.rowGroupingModel,
    sortModel: applied.sortModel,
  });
}
